import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ForkKnife,
  Plus,
  Minus,
  ShoppingCart,
  CheckCircle,
  Clock,
  Hourglass,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

/**
 * Public QR ordering page. URL: /qr/:outletId?table=...
 * Customers scan a QR code on the table, see the menu, place an order,
 * and watch live status — all without logging in.
 */
const QRMenuPage = () => {
  const { outletId } = useParams();
  const [search] = useSearchParams();
  const tableId = search.get('table');

  const [outlet, setOutlet] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [cart, setCart] = useState({}); // { itemId: { qty, item } }
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [placedOrder, setPlacedOrder] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/qr/${outletId}/menu`);
        setOutlet(r.data.outlet);
        setCategories(r.data.categories);
        setItems(r.data.items);
        if (r.data.categories?.length) setActiveCat(r.data.categories[0].id);
      } catch (e) {
        toast.error('Could not load menu');
      }
    };
    load();
  }, [outletId]);

  // Poll order status once an order is placed
  useEffect(() => {
    if (!placedOrder?.order_id) return;
    const fetchStatus = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/qr/orders/${placedOrder.order_id}/status`);
        setPlacedOrder((prev) => ({ ...prev, status: r.data.status, items: r.data.items }));
      } catch (e) { /* ignore */ }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 5000);
    return () => clearInterval(t);
  }, [placedOrder?.order_id]);

  const filtered = useMemo(
    () => (activeCat ? items.filter((i) => i.category_id === activeCat) : items),
    [items, activeCat]
  );

  const inc = (item) =>
    setCart((c) => ({ ...c, [item.id]: { item, qty: (c[item.id]?.qty || 0) + 1 } }));
  const dec = (item) =>
    setCart((c) => {
      const cur = c[item.id]?.qty || 0;
      if (cur <= 1) {
        const { [item.id]: _omit, ...rest } = c;
        return rest;
      }
      return { ...c, [item.id]: { item, qty: cur - 1 } };
    });

  const subtotal = useMemo(
    () => Object.values(cart).reduce((s, e) => s + (e.item.dine_in_price || 0) * e.qty, 0),
    [cart]
  );

  const placeOrder = async () => {
    if (!Object.keys(cart).length) {
      toast.error('Cart is empty');
      return;
    }
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    try {
      const payload = {
        table_id: tableId,
        customer_name: name,
        customer_phone: phone,
        items: Object.values(cart).map((e) => ({
          menu_item_id: e.item.id,
          quantity: e.qty,
          modifiers: [],
        })),
      };
      const r = await axios.post(`${API_URL}/api/qr/${outletId}/orders`, payload);
      setPlacedOrder(r.data);
      setCart({});
      toast.success(`Order placed: ${r.data.order_number}`);
    } catch (e) {
      toast.error('Could not place order');
    }
  };

  // ---- Order status view ----
  if (placedOrder) {
    const statusMap = {
      new: { label: 'Order received', icon: Clock, color: '#fbbf24' },
      confirmed: { label: 'Confirmed', icon: Clock, color: '#fbbf24' },
      preparing: { label: 'Preparing', icon: Hourglass, color: '#f97316' },
      ready: { label: 'Ready to serve', icon: CheckCircle, color: '#22c55e' },
      completed: { label: 'Served', icon: CheckCircle, color: '#22c55e' },
    };
    const s = statusMap[placedOrder.status] || statusMap.new;
    const Icon = s.icon;
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 text-white p-6 flex flex-col items-center" data-testid="qr-status-page">
        <div className="max-w-md w-full rounded-2xl bg-white/5 border border-white/10 p-6 mt-12 text-center">
          <h1 className="text-2xl font-bold mb-1">{outlet?.name}</h1>
          <p className="text-white/60 text-sm mb-6">Order #{placedOrder.order_number}</p>
          <div className="flex flex-col items-center gap-3 mb-6">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${s.color}25`, color: s.color }}
            >
              <Icon size={40} weight="duotone" />
            </div>
            <p className="text-lg font-semibold" style={{ color: s.color }}>{s.label}</p>
            <p className="text-3xl font-extrabold mt-2">Token {placedOrder.token_number}</p>
          </div>
          <p className="text-white/70 text-sm mb-4">Total: ${placedOrder.total?.toFixed(2)}</p>
          <Button
            onClick={() => setPlacedOrder(null)}
            variant="outline"
            className="bg-transparent border-white/30 text-white hover:bg-white/10"
            data-testid="qr-new-order-btn"
          >
            Place another order
          </Button>
        </div>
      </div>
    );
  }

  // ---- Menu view ----
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 text-white pb-32" data-testid="qr-menu-page">
      <header className="px-5 py-6 border-b border-white/10 sticky top-0 backdrop-blur bg-slate-900/80 z-20">
        <p className="text-orange-300 text-xs uppercase tracking-widest mb-1">Welcome to</p>
        <h1 className="text-3xl font-extrabold">{outlet?.name || 'Menu'}</h1>
        <p className="text-white/60 text-sm mt-1">
          {tableId ? `Table ordering · ID ${tableId.slice(-4)}` : 'Order from your phone'}
        </p>
      </header>

      {/* Category tabs */}
      <div className="px-5 py-3 flex gap-2 overflow-x-auto scrollbar-hide sticky top-[88px] bg-slate-900/80 backdrop-blur z-10">
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setActiveCat(c.id)}
            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-sm transition ${
              activeCat === c.id ? 'bg-orange-500 text-white' : 'bg-white/10 text-white/80'
            }`}
            data-testid={`qr-cat-${c.id}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Items */}
      <div className="px-5 py-4 space-y-3">
        {filtered.map((it) => (
          <div
            key={it.id}
            className="rounded-xl bg-white/5 border border-white/10 p-4 flex items-center gap-4"
            data-testid={`qr-item-${it.id}`}
          >
            <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-300">
              <ForkKnife size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{it.name}</p>
              <p className="text-white/60 text-xs line-clamp-1">{it.description}</p>
              <p className="text-orange-300 font-bold mt-1">${(it.dine_in_price || 0).toFixed(2)}</p>
            </div>
            {cart[it.id] ? (
              <div className="flex items-center gap-2">
                <button onClick={() => dec(it)} className="w-8 h-8 rounded-full bg-white/10" data-testid={`qr-dec-${it.id}`}>
                  <Minus size={14} className="mx-auto" />
                </button>
                <span className="min-w-[20px] text-center font-semibold">{cart[it.id].qty}</span>
                <button onClick={() => inc(it)} className="w-8 h-8 rounded-full bg-orange-500" data-testid={`qr-inc-${it.id}`}>
                  <Plus size={14} className="mx-auto" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => inc(it)}
                className="px-3 py-2 rounded-lg bg-orange-500 text-white font-medium text-sm"
                data-testid={`qr-add-${it.id}`}
              >
                Add
              </button>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="text-white/60 text-center py-8">No items in this category yet.</p>
        )}
      </div>

      {/* Cart footer */}
      {Object.keys(cart).length > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur border-t border-white/10 p-4 z-30"
          data-testid="qr-cart-bar"
        >
          <div className="flex gap-2 mb-3">
            <Input
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
              data-testid="qr-name-input"
            />
            <Input
              placeholder="Phone (optional)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
              data-testid="qr-phone-input"
            />
          </div>
          <Button
            onClick={placeOrder}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 font-bold"
            data-testid="qr-place-order-btn"
          >
            <ShoppingCart size={18} className="mr-2" />
            Place Order — ${subtotal.toFixed(2)}
          </Button>
        </div>
      )}
    </div>
  );
};

export default QRMenuPage;
